import * as go from 'gojs';
import * as React from 'react';

import { refreshProjectModel } from 'src/actions/projects/action-creators/refreshProjectModel';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { addProjectModelsToDiagramModel } from 'src/containers/ProjectDiagram/utils/addProjectModelsToDiagramModel';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { hasModelChangedFromDraft } from 'src/containers/ProjectDiagram/utils/detectModelChanges';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import { removeStaleModelConcepts } from 'src/containers/ProjectDiagram/utils/removeStaleModelConcepts';
import { addSensitivePropertyGroupsFromDatabase } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/addSensitivePropertyGroupsFromDatabase';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { projectModelSelector } from 'src/selectors/projectModel';
import { connectToStardog } from 'src/store/connections/connectToStardog';
import { coercePortalConnection } from 'src/store/connections/types';
import { ReduxState } from 'src/store/types';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useRefreshProjectModel = (modelId: string) => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const { projectId } = useRouterParams();
  const projectModel = useReduxSelector(
    projectModelSelector(projectId, modelId)
  );
  const portalConnections = usePortalConnectionsWithDemo();
  const defaultPortalConnection = useDefaultPortalConnection();
  const isMounted = useIsMounted();

  const currentConnection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isSignInDialogOpen = useReduxSelector(
    (state: ReduxState) => state.ui.isSignInDialogOpen
  );

  const [isRefreshingModel, setIsRefreshingModel] = React.useState(false);
  const [shouldRefresh, setShouldRefresh] = React.useState(false);
  const prevIsSignInDialogOpen = usePrevious(isSignInDialogOpen);

  // Derive connection from project model
  const getConnection = React.useCallback(() => {
    if (!projectModel) {
      return null;
    }
    const portalConnection =
      portalConnections?.find(({ id }) => id === projectModel.connectionId) ||
      defaultPortalConnection;
    const connection = portalConnection
      ? coercePortalConnection(portalConnection)
      : null;

    // If connection matches current but lacks credentials, reuse current
    if (
      currentConnection &&
      connection &&
      currentConnection.endpoint === connection.endpoint &&
      currentConnection.username === connection.username &&
      (!connection.password || !connection.token)
    ) {
      return currentConnection;
    }

    return connection;
  }, [
    currentConnection,
    defaultPortalConnection,
    portalConnections,
    projectModel,
  ]);

  // Perform the actual refresh once connected
  const performRefresh = React.useCallback(async () => {
    const connection = getConnection();
    if (!diagram || !projectId || !projectModel || !connection) {
      return;
    }

    try {
      const { refreshedModel, database } = await dispatch(
        refreshProjectModel({
          projectId,
          modelId,
          connection,
        })
      );

      if (!isMounted.current) {
        return;
      }

      diagram.undoManager.clear();

      let hasChanges = false;
      commitDiagramModel(
        diagram,
        (model) => {
          // Create draft data snapshot before applying changes
          const draftData = createDraftData(model);

          addProjectModelsToDiagramModel(
            [refreshedModel],
            null,
            true
          )(model, diagram);

          if (database) {
            // Sync sensitive property groups from database.
            // Must be called after model data is added, since SPGs reference attributes.
            addSensitivePropertyGroupsFromDatabase(database)(model);
          }

          removeStaleModelConcepts(refreshedModel, modelId)(model);

          // Detect if anything changed by comparing current state to draft
          hasChanges = hasModelChangedFromDraft(draftData, model);

          // Only enter draft state if there are changes
          if (hasChanges) {
            setDataProperty<ModelData>(model)(
              model.modelData,
              'draftData',
              draftData
            );
          }
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );

      if (hasChanges) {
        diagram.zoomToFit();
        diagram.alignDocument(go.Spot.Center, go.Spot.Center);
      } else {
        dispatch(projectsActionCreators.refreshProjectModelNoChanges());
      }
    } catch (e) {
      console.warn(e);
      dispatch(projectsActionCreators.refreshProjectModelFailed());
    } finally {
      if (isMounted.current) {
        setIsRefreshingModel(false);
      }
    }
  }, [
    diagram,
    dispatch,
    getConnection,
    isMounted,
    modelId,
    projectId,
    projectModel,
  ]);

  // Effect: Proceed with refresh once connected
  React.useEffect(() => {
    if (shouldRefresh && isConnected) {
      setShouldRefresh(false);
      performRefresh();
    }
  }, [shouldRefresh, isConnected, performRefresh]);

  // Effect: Cancel refresh if auth dialog closed without connecting
  // Only cancel if dialog closed AND we're not connected AND not currently connecting
  React.useEffect(() => {
    if (
      prevIsSignInDialogOpen &&
      !isSignInDialogOpen &&
      shouldRefresh &&
      !isConnected &&
      !isConnecting
    ) {
      // User closed dialog without authenticating
      setShouldRefresh(false);
      setIsRefreshingModel(false);
    }
  }, [
    isSignInDialogOpen,
    prevIsSignInDialogOpen,
    shouldRefresh,
    isConnected,
    isConnecting,
  ]);

  const refreshModel = React.useCallback(() => {
    if (!diagram || !projectId || !projectModel) {
      return;
    }

    const { databaseId, importedModelName } = projectModel;
    if (!databaseId || !importedModelName) {
      return;
    }

    const connection = getConnection();
    if (!connection) {
      dispatch(projectsActionCreators.refreshProjectModelFailed());
      return;
    }

    setIsRefreshingModel(true);
    setShouldRefresh(true);

    // Try to connect - will either succeed or open auth dialog
    dispatch(connectToStardog({ connection, shouldNotNotifySuccess: true }));
  }, [diagram, dispatch, getConnection, projectId, projectModel]);

  return {
    refreshModel,
    isRefreshingModel,
  };
};
